import dayjs from 'dayjs';

import { DatePreset, formatHint, monthYearFormat, presetFormat, typedDateFormats } from '@server/utility/date-formats';

// dayjs plugins (utc, timezone, customParseFormat, ...) are registered once,
// before the app loads, in locale-bootstrap.ts — don't extend them here.

/**
 * Centralized date utilities for consistent date handling across the application.
 * 
 * IMPORTANT CONVENTIONS:
 * - Clients send dates to the backend in ISO 8601 format with timezone information
 * - The server is responsible for converting to UTC for MySQL storage
 * - This ensures timezone information is preserved and conversion is centralized
 */

/**
 * Get the user's IANA timezone identifier (e.g., "America/New_York", "America/Los_Angeles").
 * This is used to store the timezone context for dates in the database.
 * Falls back to "America/New_York" if detection fails.
 * 
 * @returns IANA timezone string
 * 
 * @example
 * getUserTimezone(); // Returns: "America/Chicago" (based on browser/system)
 */
export const getUserTimezone = (): string => {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
        return tz;
    } catch {
        return 'America/New_York';
    }
};

/**
 * Format a date for sending to the backend.
 * Converts to ISO 8601 format with timezone information.
 * The server will handle UTC conversion for database storage.
 * 
 * @param date - Date object, string, or dayjs object to format
 * @returns ISO 8601 datetime string with timezone (e.g., '2026-04-16T10:30:00-05:00')
 * 
 * @example
 * // User selects "2026-04-16 10:30 AM" in their local timezone (EST)
 * formatDateForBackend(new Date(2026, 3, 16, 10, 30)); 
 * // Returns: '2026-04-16T10:30:00-05:00' (preserves timezone info)
 */
export const formatDateForBackend = (date: Date | string | dayjs.Dayjs | null | undefined): string => {
    if (!date) {
        return new Date().toISOString();
    }
    
    // If it's already a Date object, use toISOString
    if (date instanceof Date) {
        return date.toISOString();
    }
    
    // If it's a string or dayjs object, convert to Date and use toISOString
    return dayjs(date).toDate().toISOString();
};

// The locale is set before the app loads (locale-bootstrap.ts) and changes only with a page reload, so
// its patterns are read once and kept.
const localePatterns = new Map<string, string>();
const localePattern = (token: 'L' | 'll'): string => {
    let pattern = localePatterns.get(token);
    if (pattern === undefined) {
        pattern = dayjs().localeData().longDateFormat(token);
        localePatterns.set(token, pattern);
    }
    return pattern;
};

/**
 * The dayjs format string for a preset in the active locale (LIQ-2057), for values that
 * `formatDisplayDate` can't take as they are, e.g. a time shown in another timezone:
 * `dayjs(start).tz(event.timezone).format(displayFormat('time'))`.
 */
export const displayFormat = (preset: DatePreset): string => presetFormat(preset, localePattern('ll'));

/**
 * Format a date for people to read, by preset name, in the active locale (LIQ-2056). Use this (or the
 * `liqDate` pipe) for anything shown to users; keep literal ISO formats for API parameters and keys.
 *
 * @param date - Date, ISO string, epoch milliseconds or dayjs value
 * @param preset - Named format; see `DatePreset` (default `date`: the locale's numeric short date)
 * @param options - `utc: true` for zone-less calendar dates (DATE columns such as "Date Added" or a
 *                  birth date), so they show the same day in every timezone; leave it off for
 *                  instants (created/updated times), which are shown in local time
 * @returns The formatted text, or '' for a missing or invalid date
 *
 * @example
 * formatDisplayDate('2026-03-04', 'date', { utc: true }); // en-NZ: '04/03/2026', en-US: '03/04/2026'
 * formatDisplayDate(row.updatedAt, 'dateTime');          // en-NZ: '04/03/2026 2:05 PM'
 */
export const formatDisplayDate = (
    date: Date | string | number | dayjs.Dayjs | null | undefined,
    preset: DatePreset = 'date',
    options: { utc?: boolean } = {}
): string => {
    if (date === null || date === undefined || date === '') {
        return '';
    }
    const value = options.utc ? dayjs.utc(date) : dayjs(date);
    if (!value.isValid()) {
        return '';
    }
    return value.format(displayFormat(preset));
};

/**
 * Today's date for a download's file name, in the user's own timezone (`2026-03-04`). Not
 * `toISOString().split('T')[0]`, which is the UTC date: a morning export in NZ was named for
 * yesterday (B18).
 */
export const fileDateStamp = (date: Date | dayjs.Dayjs = dayjs()): string => dayjs(date).format('YYYY-MM-DD');

/**
 * Parse a UTC datetime string from the backend into a local Date object.
 * The backend sends UTC strings in 'YYYY-MM-DD HH:mm:ss' format.
 * 
 * @param mysqlDateTime - MySQL datetime string in 'YYYY-MM-DD HH:mm:ss' format (UTC)
 * @returns Date object in local timezone
 */
export const parseMySQLDateTime = (mysqlDateTime: string | null | undefined): Date | null => {
    if (!mysqlDateTime) {
        return null;
    }
    // Parse as UTC by adding 'Z' suffix, then JavaScript Date handles local conversion
    return new Date(mysqlDateTime.replace(' ', 'T') + 'Z');
};

/**
 * Parse an arbitrary date-like input and return a valid dayjs instance, or
 * `null` when the value cannot be parsed into a real calendar date.
 *
 * Centralises the "parse → `isValid()` → skip on false" pattern that shows
 * up any time we iterate over API rows (many of which can carry `null`,
 * empty strings, or legacy numeric month values from stale caches).
 *
 * By default the value is parsed in UTC so day/month bucketing is stable
 * regardless of the browser's local timezone — server DATE columns are
 * calendar dates without a zone, and interpreting them locally would push
 * rows near midnight into the wrong day/month in negative-offset zones.
 * Callers that specifically need local-zone parsing can pass
 * `{ utc: false }`.
 *
 * @param value    Raw value from the API (string, Date, dayjs, number, etc.).
 * @param options  `utc` (default `true`) controls whether the value is
 *                 interpreted in UTC.
 * @returns A valid dayjs instance, or `null` for missing / invalid input.
 *
 * @example
 * for (const row of rows) {
 *     const parsed = parseValidDate(row.Date);
 *     if (!parsed) continue;
 *     bucket.set(parsed.format('YYYY-MM'), ...);
 * }
 */
export const parseValidDate = (
    value: string | number | Date | dayjs.Dayjs | null | undefined,
    options: { utc?: boolean } = {}
): dayjs.Dayjs | null => {
    // Reject the obvious no-value cases up front — `dayjs(null)` and
    // `dayjs(undefined)` both return "now", which would silently produce a
    // valid-looking but wrong result. Empty strings hit the same trap.
    if (value === null || value === undefined || value === '') return null;

    const useUtc = options.utc !== false;
    const parsed = useUtc ? dayjs.utc(value) : dayjs(value);
    return parsed.isValid() ? parsed : null;
};

/**
 * The active locale's numeric short date pattern, in dayjs tokens: `MM/DD/YYYY` (en-US, es-US),
 * `DD/MM/YYYY` (en-NZ, es) or `YYYY-MM-DD` (sv). Inputs show and read dates in it.
 */
export const localeDateFormat = (): string => localePattern('L');

/** The active locale's numeric month-and-year pattern, e.g. `MM/YYYY` or `YYYY-MM`. */
export const localeMonthYearFormat = (): string => monthYearFormat(localeDateFormat());

/**
 * A format written for people (placeholder or hint), in the letters of the locale's language:
 * `DD/MM/YYYY` (en-NZ), `ÅÅÅÅ-MM-DD` (sv), `MM/DD/AAAA` (es-US). Defaults to the short date pattern.
 */
export const localeFormatHint = (format: string = localeDateFormat()): string => formatHint(format, dayjs.locale());

/**
 * Reads a typed date strictly in `format` (default: the locale's short date), also accepting
 * single-digit days and months. Text in another order, or an impossible date, comes back invalid
 * instead of rolling over to some other date. Check `isValid()` on the result.
 */
export const parseTypedDate = (text: string, format: string = localeDateFormat()): dayjs.Dayjs =>
    dayjs(text.trim(), typedDateFormats(format), true);
